import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { GraduationCap, Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";

interface EducationEntryProps {
  edu: {
    id?: string;
    degree: string;
    fieldOfStudy: string;
    institution: string;
    startDate: string;
    endDate?: string;
    isCurrent: boolean;
    description?: string;
  };
}

export function EducationEntry({ edu }: EducationEntryProps) {
  const { t } = useTranslation(["talent", "common"]);

  return (
    <div className="relative group">
      {/* Icon/Dot */}
      <div className="absolute left-[-32px] top-0 flex items-center justify-center w-6 h-6 rounded-full border-2 border-white bg-gray-100 ring-4 ring-white shrink-0 z-10">
        <GraduationCap className="w-3 h-3 text-gray-500" />
      </div>

      {/* Content */}
      <Card className="border shadow-sm hover:shadow-md transition-shadow">
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
            <div>
              <h4 className="font-bold text-gray-900 text-lg leading-tight mb-1">
                {edu.degree} - {edu.fieldOfStudy}
              </h4>
              <div className="text-primary font-medium text-base">
                {edu.institution}
              </div>
            </div>

            <div className="flex flex-col items-start sm:items-end gap-2 shrink-0">
              {edu.isCurrent && (
                <Badge
                  variant="default"
                  className="bg-green-600 hover:bg-green-700"
                >
                  {t("talent:experience.present")}
                </Badge>
              )}
              <div className="flex items-center text-sm text-gray-500 font-medium uppercase tracking-wider">
                <Calendar className="w-3.5 h-3.5 mr-1.5" />
                <span>
                  {new Date(edu.startDate).toLocaleDateString()}
                  {" - "}
                  {edu.isCurrent
                    ? t("talent:experience.present")
                    : edu.endDate
                      ? new Date(edu.endDate).toLocaleDateString()
                      : ""}
                </span>
              </div>
            </div>
          </div>

          {edu.description && (
            <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap border-t pt-4">
              {edu.description}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
